import { type Action, combineReducers, configureStore, type Middleware, type Reducer, type ThunkDispatch } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import { type TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { persistReducer, persistStore } from "redux-persist";
import { authPersistConfig, authSlice, logOut } from "@/src/auth/store/auth-slice";
import { env } from "@/src/common/constants/env";
import { baseApi } from "@/src/common/services/base-api";
import { generalPersistConfig, generalSlice } from "@/src/common/store/general-slice";
import { communityPersistConfig, communitySlice } from "@/src/community/store/community-slice";
import { eventSlice } from "@/src/event/store/event-slice";
import { meetingSlice } from "@/src/meeting/store/meeting-slice";
import { memberListSlice } from "@/src/member/store/member-list-slice";
import { messageSlice } from "@/src/message/store/message-slice";
import { onboardingSlice } from "@/src/onboarding/store/onboarding-slice";
import { postFeedSlice } from "@/src/post/store/post-feed-slice";
import { presenceSlice } from "@/src/presence/store/presence-slice";

const apiReducers = {
	[baseApi.reducerPath]: baseApi.reducer,
} satisfies { [Key in typeof baseApi.reducerPath]: typeof baseApi.reducer };

const combinedReducer = combineReducers({
	[generalSlice.name]: persistReducer(generalPersistConfig, generalSlice.reducer),
	[authSlice.name]: persistReducer(authPersistConfig, authSlice.reducer),
	[communitySlice.name]: persistReducer(communityPersistConfig, communitySlice.reducer),
	[onboardingSlice.name]: onboardingSlice.reducer,
	[postFeedSlice.name]: postFeedSlice.reducer,
	[presenceSlice.name]: presenceSlice.reducer,
	[eventSlice.name]: eventSlice.reducer,
	[meetingSlice.name]: meetingSlice.reducer,
	[memberListSlice.name]: memberListSlice.reducer,
	[messageSlice.name]: messageSlice.reducer,
	...apiReducers,
});

type RootStateShape = ReturnType<typeof combinedReducer>;

const rootReducer: Reducer<RootStateShape> = (state, action) => {
	if (action.type === logOut.type && state) {
		const lastUsedAuthMethod = state.auth.lastUsedAuthMethod;
		const cleared = combinedReducer(undefined, action);
		return {
			...cleared,
			auth: { ...cleared.auth, lastUsedAuthMethod },
		};
	}
	return combinedReducer(state, action);
};

export const store = configureStore({
	reducer: rootReducer,
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware({
			serializableCheck: false,
		}).concat(baseApi.middleware),
	devTools: env.MODE !== "production",
});

export const persistor = persistStore(store);

setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export type AppMiddlewareDispatch = ThunkDispatch<RootState, undefined, Action>;
export type AppMiddleware = Middleware<object, RootState, AppMiddlewareDispatch>;
